import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useTheme } from "../contexts/ThemeContext";
import axios from "axios";
import { 
  FileText, 
  LogOut, 
  ExternalLink, 
  Sun,
  Moon,
  Menu,
  X,
  ChevronDown,
  Check,
  Mail,
  Copy
} from "lucide-react";
import { Button } from "../components/ui/button";

const ClientPortal = () => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [orgSlug, setOrgSlug] = useState(null);
  const [accountSlug, setAccountSlug] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountData, setAccountData] = useState(null);
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState({});
  
  // Check impersonation status
  useEffect(() => {
    const checkImpersonation = () => {
      const impersonating = localStorage.getItem("impersonating") === "true";
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      setIsImpersonating(impersonating);
      setImpersonatedUser(storedUser);
    };
    checkImpersonation();
    const interval = setInterval(checkImpersonation, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Handle exit impersonation
  const handleExitImpersonation = () => {
    const adminToken = localStorage.getItem("admin_token_backup");
    const adminUser = localStorage.getItem("admin_user_backup");
    if (adminToken && adminUser) {
      localStorage.setItem("token", adminToken);
      localStorage.setItem("user", adminUser);
    }
    localStorage.removeItem("admin_token_backup");
    localStorage.removeItem("admin_user_backup");
    localStorage.removeItem("impersonating");
    localStorage.removeItem("impersonated_by");
    window.location.href = "/admin";
  };
  
  // ESC key to exit impersonation
  useEffect(() => {
    if (!isImpersonating) return;
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') handleExitImpersonation();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isImpersonating]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        
        const calldrip_org_id = user.calldrip_org_id;
        const calldrip_account_id = user.calldrip_account_id;
        
        if (!calldrip_org_id || !calldrip_account_id) {
          console.error("Missing org or account ID");
          setLoading(false);
          return;
        }
        
        // Fetch reports
        const reportsRes = await axios.get(`${API}/external/reports/by-ids`, {
          params: { 
            calldrip_org_id: calldrip_org_id,
            calldrip_account_id: calldrip_account_id
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const fetchedReports = reportsRes.data.reports || [];
        setReports(fetchedReports);
        
        if (fetchedReports.length > 0) {
          setOrgSlug(fetchedReports[0].org_slug);
          setAccountSlug(fetchedReports[0].account_slug);
        }
        
        // Auto-select the current report
        const currentReport = fetchedReports.find(r => r.is_current);
        if (currentReport) {
          setSelectedReport(currentReport);
        } else if (fetchedReports.length > 0) {
          setSelectedReport(fetchedReports[0]);
        }

        // Fetch account data for logo
        try {
          const accountRes = await axios.get(`${API}/external/account-info`, {
            params: { calldrip_account_id },
            headers: { Authorization: `Bearer ${token}` }
          });
          setAccountData(accountRes.data);
        } catch (e) {
          // Account info endpoint might not exist yet
          console.log("Account info not available");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.is_external) {
      fetchData();
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  const getReportUrl = () => {
    if (!orgSlug || !accountSlug) return '';
    return `${window.location.origin}/reports/${orgSlug}/${accountSlug}`;
  };

  const selectReport = (report) => {
    setSelectedReport(report);
    setMenuOpen(false);
  };

  // Generate email-friendly version of the report
  const openEmailVersion = async () => {
    if (!orgSlug || !accountSlug || !selectedReport) return;
    
    try {
      // Fetch the report HTML
      const response = await axios.get(
        `${API}/external/reports/${orgSlug}/${accountSlug}/html?period=${encodeURIComponent(selectedReport.period)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      const darkHtml = response.data;
      
      // Convert dark theme to light theme for email
      let emailHtml = darkHtml
        // Background colors
        .replace(/background:\s*#0f1923/g, 'background: #f8fafc')
        .replace(/background:\s*#1a2736/g, 'background: #ffffff')
        .replace(/background:\s*#253545/g, 'background: #f1f5f9')
        .replace(/background:\s*rgba\(42,\s*58,\s*74[^)]*\)/g, 'background: rgba(241, 245, 249, 0.5)')
        // Text colors  
        .replace(/color:\s*#e0e0e0/g, 'color: #1e293b')
        .replace(/color:\s*#8899aa/g, 'color: #64748b')
        .replace(/color:\s*#6a7a8a/g, 'color: #94a3b8')
        // Border colors
        .replace(/border[^:]*:\s*[^;]*#2a3a4a/g, 'border: 1px solid #e2e8f0')
        .replace(/#2a3a4a/g, '#e2e8f0')
        // Body background
        .replace(/body\s*{[^}]*background:\s*#0f1923/g, 'body { background: #f8fafc')
        // Keep accent colors (orange, green, etc) but ensure good contrast
        .replace(/\.green\s*{\s*color:\s*#4caf50/g, '.green { color: #16a34a')
        .replace(/\.red\s*{\s*color:\s*#ef5350/g, '.red { color: #dc2626');
      
      // Add email-friendly notice at top
      const emailNotice = `
        <div style="background: #fef3c7; border: 1px solid #fcd34d; border-radius: 8px; padding: 12px 16px; margin: 20px auto; max-width: 1400px; text-align: center;">
          <p style="margin: 0; color: #92400e; font-size: 13px;">
            <strong>📧 Email-Friendly Version</strong> — Select all (Cmd+A) and copy (Cmd+C) to paste into an email
          </p>
        </div>
      `;
      emailHtml = emailHtml.replace(/<body([^>]*)>/, `<body$1>${emailNotice}`);
      
      // Open in new window
      const newWindow = window.open('', '_blank');
      newWindow.document.write(emailHtml);
      newWindow.document.close();
    } catch (error) {
      console.error('Error generating email version:', error);
      alert('Failed to generate email version. Please try again.');
    }
  };

  if (!user?.is_external) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <p className="text-slate-400">Access denied. This portal is for external clients only.</p>
      </div>
    );
  }

  // Client's custom logo - prefer base64 data (portable), fallback to URL (local only)
  const clientLogo = accountData?.logo_data 
    ? accountData.logo_data  // Base64 data URL works everywhere
    : accountData?.logo_url 
      ? `${API.replace('/api', '')}${accountData.logo_url}` 
      : null;
  const accountName = user.account_name || 'Client Portal';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </div>
            <div>
              <span className="font-semibold text-sm">Viewing as: {impersonatedUser?.name || 'User'}</span>
              <span className="text-white/80 text-xs ml-2">({impersonatedUser?.email})</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/70 text-xs hidden sm:inline">Press ESC or click →</span>
            <button
              onClick={handleExitImpersonation}
              className="bg-white text-amber-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-amber-100 transition-colors flex items-center gap-2 shadow-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" x2="9" y1="12" y2="12"/>
              </svg>
              Exit Impersonation
            </button>
          </div>
        </div>
      )}
      
      {/* Compact Header */}
      <header className={`sticky ${isImpersonating ? 'top-[60px]' : 'top-0'} z-50 ${isDark ? 'bg-slate-800/95 backdrop-blur border-b border-slate-700' : 'bg-white/95 backdrop-blur border-b border-slate-200'}`}>
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left: Hamburger + Logo */}
          <div className="flex items-center gap-3">
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            
            {/* Client Logo or Name */}
            <div className="flex items-center gap-2">
              {clientLogo ? (
                <img src={clientLogo} alt={accountName} className="h-8 max-w-[120px] object-contain" />
              ) : (
                <span className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {accountName}
                </span>
              )}
            </div>
          </div>
          
          {/* Right: Theme + Logout */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            
            <button
              onClick={handleLogout}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>

        {/* Dropdown Menu */}
        {menuOpen && (
          <div className={`absolute top-full left-0 right-0 ${isDark ? 'bg-slate-800 border-b border-slate-700' : 'bg-white border-b border-slate-200'} shadow-lg`}>
            <div className="p-4">
              <div className={`text-xs uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Reports
              </div>
              {reports.length === 0 ? (
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No reports available</p>
              ) : (
                <div className="space-y-1">
                  {reports.map((report) => (
                    <button
                      key={report.id}
                      onClick={() => selectReport(report)}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition-colors ${
                        selectedReport?.id === report.id
                          ? isDark 
                            ? 'bg-cyan-600/20 text-cyan-300' 
                            : 'bg-cyan-50 text-cyan-700'
                          : isDark
                            ? 'hover:bg-slate-700 text-slate-300'
                            : 'hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText size={16} />
                        <div>
                          <p className="text-sm font-medium">{report.title}</p>
                          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{report.period}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {report.is_current && (
                          <span className={`text-[10px] px-2 py-0.5 rounded ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-600'}`}>
                            Latest
                          </span>
                        )}
                        {selectedReport?.id === report.id && <Check size={16} className="text-cyan-500" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* User Info */}
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Logged in as
                </div>
                <div className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  {user.name}
                </div>
                <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  {user.email}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content - Full Screen Report */}
      <main className="h-[calc(100vh-56px)]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="loading-spinner border-t-cyan-500"></div>
          </div>
        ) : reports.length === 0 ? (
          <div className={`flex flex-col items-center justify-center h-full px-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            <FileText className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">No reports available</p>
            <p className="text-sm mt-1 text-center">Your reports will appear here once they're published</p>
          </div>
        ) : selectedReport ? (
          <div className="h-full flex flex-col">
            {/* Report Header Bar */}
            <div className={`flex items-center justify-between px-4 py-2 ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-2 min-w-0">
                <button 
                  onClick={() => setMenuOpen(true)}
                  className={`flex items-center gap-1 text-sm ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  <span className="truncate max-w-[200px]">{selectedReport.title}</span>
                  <ChevronDown size={16} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openEmailVersion}
                  className={`text-xs ${isDark ? 'border-amber-600 text-amber-400 hover:bg-amber-500/20' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}`}
                >
                  <Mail size={14} className="mr-1" />
                  Email Copy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(getReportUrl(), '_blank')}
                  className={`text-xs ${isDark ? 'border-slate-600 hover:bg-slate-700' : ''}`}
                >
                  <ExternalLink size={14} className="mr-1" />
                  Full Screen
                </Button>
              </div>
            </div>
            
            {/* Embedded Report - Takes remaining height */}
            <div className="flex-1 relative">
              <iframe
                src={getReportUrl()}
                title={selectedReport.title}
                className="w-full h-full border-0"
                style={{ background: isDark ? '#0f172a' : 'white' }}
              />
            </div>
            
            {/* Powered by Footer */}
            <div className={`flex items-center justify-center gap-2 py-2 ${isDark ? 'bg-slate-800/80 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>
              <span className="text-xs">Powered by</span>
              <img 
                src="https://customer-assets.emergentagent.com/job_sales-scorecards/artifacts/8h0qwbfz_calldrip-logo-white%20%282%29.png"
                alt="Calldrip" 
                className={`h-4 ${!isDark ? 'brightness-0 opacity-50' : 'opacity-70'}`}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Select a report from the menu</p>
          </div>
        )}
      </main>

      {/* Click outside to close menu */}
      {menuOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default ClientPortal;
